import { useEffect, useRef } from "react";
import { useJourneyStore } from "../game/state/journeyMachine";

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

export function HelpOverlay() {
  const helpOpen = useJourneyStore((s) => s.helpOpen);
  const setHelpOpen = useJourneyStore((s) => s.setHelpOpen);
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const previousFocus = useRef<Element | null>(null);

  // Guarda el foco previo, enfoca el diálogo y lo restituye al cerrar (WCAG 2.4.3).
  useEffect(() => {
    if (!helpOpen) return;
    previousFocus.current = document.activeElement;
    closeRef.current?.focus();
    return () => {
      (previousFocus.current as HTMLElement | null)?.focus?.();
    };
  }, [helpOpen]);

  // Focus trap: Tab y Shift+Tab ciclan dentro del diálogo (APG Dialog).
  useEffect(() => {
    if (!helpOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setHelpOpen(false);
        return;
      }
      if (e.key !== "Tab" || !dialogRef.current) return;
      const items = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      } else if (!dialogRef.current.contains(document.activeElement)) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [helpOpen, setHelpOpen]);

  if (!helpOpen) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="help-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) setHelpOpen(false);
      }}
    >
      <div ref={dialogRef} className="max-w-md rounded-[var(--radius-md)] bg-[var(--cale-surface-raised)] p-6 shadow-[var(--shadow-panel)]">
        <h2 id="help-title" className="text-lg font-semibold">
          Ayuda del recorrido
        </h2>
        <ul className="mt-3 list-disc space-y-1 pl-5 text-sm">
          <li>Flecha derecha / izquierda: cambiar de etapa.</li>
          <li>H: abrir esta ayuda. Esc: cerrar o volver a la portada.</li>
          <li>Modo guiado avanza en orden; modo libre permite saltar a cualquier etapa.</li>
          <li>Puedes alternar entre vista 3D y vista 2D accesible en cada etapa.</li>
        </ul>
        <p className="mt-3 text-xs text-[var(--cale-ink-muted)]">
          Esta es una simulación educativa sin validez oficial.
        </p>
        <button
          ref={closeRef}
          type="button"
          onClick={() => setHelpOpen(false)}
          className="mt-4 rounded-md bg-[var(--cale-accent)] px-4 py-1.5 text-sm text-white hover:bg-[var(--cale-accent-strong)]"
        >
          Cerrar
        </button>
      </div>
    </div>
  );
}
